import { type FC, type ReactNode, useState } from "react";
import {
  AlertTriangle,
  ChevronRight,
  FileIcon,
  Package,
  Paperclip,
  Pencil,
  WandSparkles,
  X,
  Zap,
} from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

import { cn } from "@/shared/utils/core";
import { getFileExtension } from "@/shared/utils/fileUtils";
import { getCategoryStyle } from "@/shared/utils/categoryColors";
import DocumentSheet from "@/shared/components/document-viewer/DocumentSheet";
import { Button } from "@/shared/components/ui/button";
import { Tag } from "@/shared/components/ui/tag";
import { getItemIcon } from "@/shared/constants/estateCategories";
import {
  isEstimatedItem,
  isUnsureEstimate,
  getEstimateRangeLabel,
} from "@/shared/utils/balanceEstimates";
import type { EstateItem, SourceDocument } from "@/shared/types/estateTypes";

import zillowIcon from "@/shared/assets/icons/zillow.svg";
import { getInstitutionLogo } from "@/shared/utils/institutionLogos";
import { formatCurrency, formatPersonDateForDisplay } from "@/shared/utils/formatters";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { IconButton } from "@/shared/components/ui/icon-button";

const TERM_REPLACEMENTS: [RegExp, string][] = [
  [/\b401k\b/gi, "401(k)"],
  [/\b403b\b/gi, "403(b)"],
  [/\b457b\b/gi, "457(b)"],
  [/\bira\b/g, "IRA"],
  [/\bIra\b/g, "IRA"],
  [/\bheloc\b/gi, "HELOC"],
  [/\betf\b/gi, "ETF"],
  [/\betfs\b/gi, "ETFs"],
  [/\bcd\b/g, "CD"],
  [/\bcds\b/g, "CDs"],
  [/\bhsa\b/gi, "HSA"],
  [/\bfsa\b/gi, "FSA"],
];

function formatItemName(name: string): string {
  let result = name;
  for (const [pattern, replacement] of TERM_REPLACEMENTS) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

function DocBadge({ documents }: { documents: SourceDocument[] }) {
  const [open, setOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<SourceDocument | null>(null);
  const count = documents.length;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="shrink-0">
            <Tag color="stone" icon={Paperclip} iconStroke={2} className="text-2xs">
              {count} {count === 1 ? "doc" : "docs"}
            </Tag>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-auto min-w-80 rounded-xl border border-primary p-4 shadow-sm"
        >
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-medium">Attached documents</p>
            <IconButton onClick={() => setOpen(false)} label="Close" tooltip={false}>
              <X />
            </IconButton>
          </div>
          <div className="flex flex-col gap-2">
            {documents.map((doc) => {
              const fileName = doc.display_name ?? doc.file_name;
              const displayName = fileName.replace(/\.[^.]+$/, "");
              const ext = getFileExtension(doc.file_name);
              return (
                <button
                  key={doc.id}
                  type="button"
                  className="flex w-full items-center gap-3 rounded-lg border border-primary bg-white p-4 transition-colors hover:bg-subtle"
                  onClick={() => {
                    setOpen(false);
                    setSelectedDoc(doc);
                  }}
                >
                  <FileIcon className="h-8 w-8 shrink-0 text-stone-200" strokeWidth={1} />
                  <div className="min-w-0 flex-1 text-left">
                    <p className="truncate text-sm">{displayName}</p>
                    <p className="text-2xs text-secondary">{ext}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {selectedDoc && (
        <DocumentSheet
          isOpen={!!selectedDoc}
          setIsOpen={(isOpen) => {
            if (!isOpen) setSelectedDoc(null);
          }}
          documentId={selectedDoc.id}
          documentName={selectedDoc.display_name ?? selectedDoc.file_name}
          documentCreatedAt={new Date(selectedDoc.created_at)}
        />
      )}
    </>
  );
}

export function ExtractedFromLink({
  sourceDocument,
  className,
}: {
  sourceDocument: SourceDocument;
  className?: string;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const displayName = sourceDocument.display_name ?? sourceDocument.file_name;

  return (
    <>
      <Button
        variant="link"
        size="sm"
        className={cn(
          "h-auto max-w-full gap-1 p-0 text-2xs text-brand hover:underline",
          className ?? "mt-1 self-start",
        )}
        onClick={() => setSheetOpen(true)}
      >
        <FileIcon className="h-3 w-3 shrink-0" />
        <span className="truncate">Extracted from {displayName}</span>
      </Button>
      <DocumentSheet
        isOpen={sheetOpen}
        setIsOpen={setSheetOpen}
        documentId={sourceDocument.id}
        documentName={displayName}
        documentCreatedAt={new Date(sourceDocument.created_at)}
      />
    </>
  );
}

interface AccountRowProps {
  item: EstateItem;
  actions?: ReactNode;
  subtitle?: string;
  onEdit?: (item: EstateItem) => void;
  onSelect?: (item: EstateItem) => void;
  hideExtractedLabel?: boolean;
  displayTitle?: string;
  displaySubtitle?: string | null;
}

export const AccountRow: FC<AccountRowProps> = ({
  item,
  actions,
  subtitle,
  onEdit,
  onSelect,
  hideExtractedLabel,
  displayTitle,
  displaySubtitle,
}) => {
  const { person } = useAppContext();
  const hasValueLabel = !!item.value_label;
  const isSmartEstimate = item.metadata?.value_source === "smart_estimator";
  const isEstimated = isEstimatedItem(item);
  const isUnsure = isUnsureEstimate(item);
  const needsValue = item.balance === 0 && !isEstimated && !hasValueLabel;
  const estimateLabel = getEstimateRangeLabel(item);
  const hardcodedLogo = getInstitutionLogo(item.item_name || "");
  const hasLogo = hardcodedLogo || item.plaid_institution_logo;
  const isDebt = item.classification === "debt";

  const Icon = getItemIcon(item.category, item.item_type) || Package;
  const style = getCategoryStyle(item.category);

  const resolvedTitle =
    displayTitle ?? formatItemName(item.item_name?.replace(/\s*\(\.{0,3}\d{4}\)\s*$/, "") ?? "");
  const resolvedSubtitle =
    isSmartEstimate && item.category === "Properties" ? null : displaySubtitle;

  return (
    <div
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect ? () => onSelect(item) : undefined}
      onKeyDown={
        onSelect
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") onSelect(item);
            }
          : undefined
      }
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-lg border bg-white p-4",
        actions && item.extraction_status === "pending" ? "border-brand" : "border-primary",
        onSelect && "cursor-pointer transition-colors hover:bg-subtle",
      )}
    >
      {hasLogo ? (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white">
          <img
            src={hardcodedLogo || `data:image/png;base64,${item.plaid_institution_logo}`}
            alt={`${item.item_name || ""} logo`}
            className="h-8 w-8 object-contain"
          />
        </div>
      ) : (
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
            style.bgClass,
          )}
        >
          <Icon className={cn("h-5 w-5", style.textClass)} />
        </div>
      )}

      <div className="flex min-w-0 flex-[1_1_40%] flex-col gap-0.5">
        <p className="flex min-w-0 items-center gap-2 font-medium">
          <span className="min-w-0 truncate">{resolvedTitle}</span>
          {item.plaid_connection_id && (
            <Zap className="ml-[5px] h-3 w-3 shrink-0 fill-stone-300 text-stone-300" />
          )}
          {item.extracted_from_document &&
            item.document_item_references &&
            item.document_item_references.length > 0 && (
              <DocBadge
                documents={item.document_item_references
                  .map((ref) => ref.document)
                  .filter((doc): doc is SourceDocument => doc !== null)
                  .filter((doc, index, self) => self.findIndex((d) => d.id === doc.id) === index)}
              />
            )}
        </p>
        <div className="flex min-w-0 items-center gap-2">
          {resolvedSubtitle ? (
            <p className="truncate text-sm text-secondary">{resolvedSubtitle}</p>
          ) : hasValueLabel ? (
            <>
              <p className="truncate text-sm text-secondary">{item.value_label}</p>
              {item.status_flag?.map((flag) => (
                <Tag key={flag} color="yellow" className="text-2xs shrink-0">
                  {flag}
                </Tag>
              ))}
            </>
          ) : item.category === "Properties" ? (
            <div className="flex min-w-0 items-center gap-1.5">
              {subtitle ? (
                <p className="truncate text-sm text-secondary">{subtitle}</p>
              ) : (
                <>
                  {item.item_type && (
                    <p className="shrink-0 text-sm text-secondary">
                      {item.item_type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                      <span className="px-1.5">&middot;</span>
                    </p>
                  )}
                  {isSmartEstimate && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <img
                            src={zillowIcon}
                            alt="Zillow estimate"
                            className="h-4 w-4 shrink-0 aspect-square"
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-sm">Estimated value from Zillow</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <p className="truncate text-sm text-secondary">{formatCurrency(item.balance)}</p>
                  {item.status_flag?.map((flag) => (
                    <Tag key={flag} color="yellow" className="text-2xs shrink-0">
                      {flag}
                    </Tag>
                  ))}
                </>
              )}
            </div>
          ) : (
            <>
              {!isUnsure && (
                <>
                  <p className="truncate text-sm text-secondary">
                    {subtitle ?? (
                      <>
                        {item.plaid_account_mask || item.identifiers?.account_last4
                          ? `••••${item.plaid_account_mask || item.identifiers?.account_last4}`
                          : ""}
                        {(item.plaid_account_mask || item.identifiers?.account_last4) &&
                        (item.balance || isEstimated) ? (
                          <span className="px-2">&middot;</span>
                        ) : (
                          ""
                        )}
                        {onSelect ? (
                          item.item_type ? (
                            <span>
                              {item.item_type
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase())}
                            </span>
                          ) : null
                        ) : (
                          <span>
                            {isEstimated
                              ? (estimateLabel ?? "Estimated")
                              : item.balance
                                ? `${isDebt ? "-" : ""}${formatCurrency(item.balance)}`
                                : ""}
                          </span>
                        )}
                      </>
                    )}
                  </p>
                  {isEstimated && (
                    <Tag color="stone" className="text-2xs">
                      Estimated
                    </Tag>
                  )}
                </>
              )}
              {isSmartEstimate && item.category === "Vehicles" && (
                <Tag color="purple" icon={WandSparkles} iconStroke={2}>
                  Elayne smart estimate
                </Tag>
              )}
              {item.status_flag?.map((flag) => (
                <Tag key={flag} color="yellow" className="text-2xs shrink-0">
                  {flag}
                </Tag>
              ))}
            </>
          )}
        </div>
        {(needsValue || (isUnsure && !hasValueLabel)) && (
          <Tag color="orange" icon={AlertTriangle} iconStroke={2} className="w-fit text-2xs">
            Value required
          </Tag>
        )}
        {!hideExtractedLabel &&
          item.extracted_from_document &&
          item.source_document &&
          item.extraction_status === "pending" && (
            <ExtractedFromLink sourceDocument={item.source_document} />
          )}
        {item.notes && item.notes !== "__MARKED_AS_NONE__" && (
          <p className="mt-1 text-sm text-tertiary">{item.notes}</p>
        )}
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-2">
        {onSelect ? (
          <>
            {!isUnsure && !isEstimated && (item.balance || hasValueLabel || needsValue) && (
              <div className="text-right whitespace-nowrap">
                <p className="text-sm font-medium">
                  {hasValueLabel
                    ? item.value_label
                    : `${isDebt && item.balance ? "-" : ""}${formatCurrency(item.balance)}`}
                </p>
                {(person?.date_of_death || item.last_updated_date) && (
                  <p className="pt-0.5 text-2xs text-tertiary">
                    as of{" "}
                    {formatPersonDateForDisplay(
                      person?.date_of_death ?? item.last_updated_date ?? "",
                    )}
                  </p>
                )}
              </div>
            )}
            {onEdit ? (
              <Button
                variant="outline"
                size="md"
                className="w-10 px-0"
                aria-label="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(item);
                }}
              >
                <Pencil className="h-4 w-4 text-tertiary" />
              </Button>
            ) : (
              <ChevronRight className="h-4 w-4 text-tertiary" />
            )}
          </>
        ) : (
          <>
            {onEdit && (
              <Button
                variant="outline"
                size="md"
                className="w-10 px-0"
                aria-label="Edit"
                onClick={() => onEdit(item)}
              >
                <Pencil className="h-4 w-4 text-tertiary" />
              </Button>
            )}
            {actions && (
              <div className="contents" onClick={(e) => e.stopPropagation()}>
                {actions}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
